import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import {
  AttachMoney as PayrollIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import TwoFactorVerification from '../components/TwoFactorVerification';
import { twoFactorService } from '../services/twoFactorService';
import { supabase } from '../services/supabase';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [twoFactorError, setTwoFactorError] = useState('');


  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();
  
  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      // Redirect to the page they tried to visit or to dashboard
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);


    // Validate inputs
    if (!email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    
    if (!password) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    try {
      // First, authenticate with Supabase
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        // Handle specific error types
        if (authError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else if (authError.message.includes('rate limit')) {
          setError('Too many login attempts. Please try again later.');
        } else {
          setError(authError.message || 'Failed to sign in');
        }
        setLoading(false);
        return;
      }

      // Get user data to check if 2FA is enabled
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (userError) {
        setError('Failed to fetch user data');
        setLoading(false);
        return;
      }

      // Check if user is admin and has 2FA enabled
      const isAdmin = userData.role === 'admin';
      const has2FA = userData.two_factor_enabled === true;
      
      if (isAdmin && has2FA) {
        // Store pending user and show 2FA verification (no signOut needed)
        setPendingUser({ email, password, userData, authData: data });
        setShowTwoFactor(true);
        setLoading(false);
        return; // Important: return here to prevent normal login flow
      } else {
        // Complete login normally
        const result = await signIn(email, password);
        if (result?.error) {
          setError(result.error.message || 'Failed to sign in');
        } else {
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        }
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
      setLoading(false);
    }
  };

  const handleTwoFactorVerify = async (code: string): Promise<boolean> => {
    setTwoFactorError('');
    
    try {
      // Verify the 2FA code using the user's stored secret
      const isValid = await twoFactorService.verifyTokenForUser(
        pendingUser.userData.id,
        code
      );

      if (isValid) {
        // 2FA verification successful - user is already authenticated
        
        // The user is already authenticated via the stored authData
        // Just clear the 2FA state and navigate
        setShowTwoFactor(false);
        setPendingUser(null);
        
        // Navigate to the intended destination
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
        return true;
      } else {
        setTwoFactorError('Invalid verification code. Please try again.');
        return false;
      }
    } catch (error) {
      setTwoFactorError('Verification failed. Please try again.');
      return false;
    }
  };

  const handleTwoFactorCancel = async () => {

    // Clear 2FA state and sign out if there's an active session
    if (pendingUser?.authData) {
      try {
        await supabase.auth.signOut();
      } catch (error) {
        console.log('Sign out error (expected):', error);
      }
    }
    setShowTwoFactor(false);
    setPendingUser(null);
    setTwoFactorError('');
  };

  // Show 2FA verification if needed
  if (showTwoFactor && pendingUser) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >

        <TwoFactorVerification
          onVerify={handleTwoFactorVerify}
          onCancel={handleTwoFactorCancel}
          loading={loading}
          error={twoFactorError}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Card
          elevation={24}
          sx={{

            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
              color: 'white',
              py: 6,
              px: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              },
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 3,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
              }}
            >
              <PayrollIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                position: 'relative',
                zIndex: 1,
              }}
            >
              Payroll System
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                fontWeight: 400,
                position: 'relative',
                zIndex: 1,
              }}
            >
              Employee Management Portal
            </Typography>
          </Box>

          <CardContent sx={{ p: 6 }}>
            {/* Features Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Enterprise Grade
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Secure payroll management
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    Secure Access
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Protected employee data
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Login Form */}
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: '#1f2937',
                  textAlign: 'center',
                }}
              >
                Sign In to Continue
              </Typography>
              
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,

                    '& .MuiAlert-icon': {
                      fontSize: '1.2rem',
                    },
                  }}
                >
                  {error}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="off"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!error && error.includes('email')}
                  disabled={loading}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {

                      backgroundColor: '#f8fafc',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
                      },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!error && error.includes('password')}
                  disabled={loading}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {

                      backgroundColor: '#f8fafc',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
                      },
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 2,

                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%)',
                      boxShadow: '0 6px 20px rgba(37, 99, 235, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: '#9ca3af',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} color="inherit" />
                      <span>Signing In...</span>
                    </Box>
                  ) : (
                    'Sign In'
                  )}
                </Button>
                
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Need help accessing your account?
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#2563eb', mt: 0.5 }}>
                    Contact your System Administrator
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;